/**
 * ConfigResolver: resolves typed config for a domain from definitions + client overrides.
 * TypeScript port of ixconfig/resolver.py (Python backend).
 *
 * Resolution algorithm per slug:
 * 1. Client disabled → null for all configs
 * 2. Feature check: isFeatureEnabled() considers requires[] deps, client override, enabled_by_default
 * 3. Merge default_config + client override via merge_strategy
 * 4. Deep-merge schema defaults under result (fills missing optional fields)
 * 5. Cache on instance
 *
 * Usage:
 *   const resolver = new ConfigResolver(domain, client, definitions, clientConfigs);
 *   const identity = resolver.identity;       // IdentityConfig (never null)
 *   const engagement = resolver.engagement;   // EngagementConfig | null (feature)
 */

import { logger } from '../../utils/core/logger';
import type {
    ActiveDomainExperiment,
    ActiveStaffExperiment,
} from '../../utils/widgetInit/experimentCohorts';
import { ConfigAccessors } from './ConfigResolver.generated';
import { applyMergeStrategy } from './merge';
import { SLUG_TO_DEFAULTS } from './registry.generated';
import type { AdminConfigRow, Client, ClientConfigRow, ConfigDefinition, Domain } from './types';

export class ConfigResolver extends ConfigAccessors {
    private _domain: Domain;
    private _client: Client;
    private _definitions: Map<string, ConfigDefinition>;
    private _overrides: Map<string, ClientConfigRow>;
    private _adminOverrides: Map<string, AdminConfigRow>;
    private _cache: Map<string, unknown>;
    private _storedCache: Map<string, unknown>;
    private _staffExperiment: ActiveStaffExperiment | null;
    private _domainExperiment: ActiveDomainExperiment | null;

    constructor(
        domain: Domain,
        client: Client,
        definitions: ConfigDefinition[],
        clientConfigs: ClientConfigRow[],
        adminConfigs: AdminConfigRow[] = [],
        staffExperiment: ActiveStaffExperiment | null = null,
        domainExperiment: ActiveDomainExperiment | null = null
    ) {
        super();
        this._domain = domain;
        this._client = client;
        this._definitions = new Map(definitions.map((d) => [d.slug, d]));
        this._overrides = new Map(clientConfigs.map((cc) => [cc.config_slug, cc]));
        this._adminOverrides = new Map(adminConfigs.map((ac) => [ac.config_slug, ac]));
        this._cache = new Map();
        this._storedCache = new Map();
        this._staffExperiment = staffExperiment;
        this._domainExperiment = domainExperiment;
    }

    /** The currently running staff (cross-site) experiment, or null. */
    get staffExperiment(): ActiveStaffExperiment | null {
        return this._staffExperiment;
    }

    /** The currently running experiment for this domain, or null. */
    get domainExperiment(): ActiveDomainExperiment | null {
        return this._domainExperiment;
    }

    /** The domain this resolver was constructed for. */
    get domain(): string {
        return this._domain.domain;
    }

    /** True when the visited domain is an alias of another (canonical_domain set). */
    get isAlias(): boolean {
        return this._domain.is_alias ?? false;
    }

    /** Workspace selected by host/path routing, when the widget RPC resolved one. */
    get workspaceId(): string | undefined {
        return this._domain.workspace_id;
    }

    /** Domain whose configuration the widget RPC selected for this visit. */
    get configDomain(): string {
        return this._domain.config_domain ?? this._domain.domain;
    }

    /** Whether the client is enabled. */
    get clientEnabled(): boolean {
        return this._client.enabled;
    }

    /** Server-computed capability, absent on older RPCs. Never infer from public config. */
    get webmcpCompareEnabled(): boolean {
        return this.isFeatureEnabled('webmcp') && this._domain.webmcp_compare_enabled === true;
    }

    /** Server-computed (free plan), absent on older RPCs. Workspace config cannot turn it off. */
    get forceDisclaimer(): boolean {
        return this._domain.force_disclaimer === true;
    }

    /**
     * Check if a feature config is enabled for this domain.
     *
     * For non-features (is_feature=false), always returns true.
     * For features: client_config.enabled → definition.enabled_by_default.
     * Also checks requires[] dependencies (recursive, with cycle detection).
     */
    isFeatureEnabled(slug: string, _visited?: Set<string>): boolean {
        if (!this._client.enabled) return false;

        const defn = this._definitions.get(slug);
        if (!defn) {
            logger.warn(`Unknown config slug: ${slug}`);
            return false;
        }

        if (!defn.is_feature) return true;

        // Check requires[] deps first (propagate visited set for cycle detection)
        if (!this._checkRequires(slug, _visited ?? new Set())) return false;

        // Client override
        const override = this._overrides.get(slug);
        if (override?.enabled !== null && override?.enabled !== undefined) {
            return override.enabled;
        }

        // Definition default
        return defn.enabled_by_default ?? true;
    }

    private _checkRequires(slug: string, visited: Set<string>): boolean {
        if (visited.has(slug)) {
            logger.error(`Circular requires dependency detected: ${slug}`);
            return false;
        }
        visited.add(slug);
        const defn = this._definitions.get(slug);
        if (!defn) return true;

        for (const depSlug of defn.requires ?? []) {
            if (!this.isFeatureEnabled(depSlug, visited)) {
                logger.debug(`Feature ${slug} disabled: dependency ${depSlug} not met`);
                return false;
            }
        }
        return true;
    }

    /**
     * Resolve and return the typed config for a slug.
     * Returns null if client is disabled or feature is disabled.
     * Caches on first access.
     */
    getConfig<T>(slug: string): T | null {
        if (this._cache.has(slug)) return this._cache.get(slug) as T | null;
        const result = this._resolve<T>(slug);
        this._cache.set(slug, result);
        return result;
    }

    /**
     * The merged config for a slug whether or not the client or feature is on.
     * Editing surfaces must seed from this, not `getConfig()`: a disabled
     * feature resolves to null there, the form falls back to schema defaults,
     * and the next save writes those defaults over the client's values (IX-5481).
     */
    getStoredConfig<T>(slug: string): T | null {
        // Cached like `getConfig`: forms reset when their `value` reference
        // changes, so a fresh object per call would discard edits on re-render.
        if (this._storedCache.has(slug)) return this._storedCache.get(slug) as T | null;
        const defn = this._definitions.get(slug);
        if (!defn) {
            logger.warn(`Unknown config slug: ${slug}`);
            return null;
        }
        const result = this._merge<T>(slug, defn);
        this._storedCache.set(slug, result);
        return result;
    }

    private _resolve<T>(slug: string): T | null {
        if (!this._client.enabled) return null;

        const defn = this._definitions.get(slug);
        if (!defn) {
            logger.warn(`Unknown config slug: ${slug}`);
            return null;
        }

        // Feature check
        if (defn.is_feature && !this.isFeatureEnabled(slug)) return null;

        return this._merge<T>(slug, defn);
    }

    private _merge<T>(slug: string, defn: ConfigDefinition): T {
        // Merge default_config + client override
        const override =
            defn.scope === 'admin' ? this._adminOverrides.get(slug) : this._overrides.get(slug);
        const overrideConfig = override?.config ?? null;
        const merged = applyMergeStrategy(defn.merge_strategy, defn.default_config, overrideConfig);

        // Apply schema defaults under merged (fills missing optional fields)
        // In Python this is handled by Pydantic model_validate() which applies field defaults.
        // In TS we deep-merge the schema DEFAULTS under the resolved config.
        const defaults = SLUG_TO_DEFAULTS[slug];
        if (defaults && Object.keys(defaults).length > 0) {
            return applyMergeStrategy('deep_merge', defaults, merged) as T;
        }
        return merged as T;
    }

    /**
     * The raw client override JSON for a slug — ignoring the admin/global default
     * and schema defaults. Use this (not a merged-vs-default diff) to tell whether
     * the client has actually customised a slug: a diff against the schema default
     * can't distinguish an admin-global default from a real client override.
     * Returns null when the client has no override row for the slug.
     */
    getClientOverride<T = Record<string, unknown>>(slug: string): T | null {
        return (this._overrides.get(slug)?.config as T) ?? null;
    }
}
